import { Loader2 } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import { Box, Text, VStack, useToast } from '@eluve/components';

export const ImpersonationLoginPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const [searchParams] = useSearchParams();
  const didImpersonate = useRef(false);

  useEffect(() => {
    const performImpersonation = async () => {
      const targetUserId = searchParams.get('targetUserId');
      const tenantId = searchParams.get('tenantId');
      const reason = searchParams.get('reason');
      const originUrl = searchParams.get('originUrl');

      if (!targetUserId || !tenantId || !reason) {
        toast.error('Invalid impersonation parameters');
        navigate('/admin');
        return;
      }

      try {
        const result = await apiClient.auth.impersonateUser({
          body: {
            targetUserId,
            tenantId,
            reason,
            originUrl: originUrl ?? undefined,
          },
        });

        if (result.status === 200) {
          toast.success(result.body.message);
          // Since we're using SuperTokens createNewSession, the new session cookies
          // are automatically set. Navigate to main app with a page reload to ensure
          // the session context picks up the new session data.
          window.location.href = `/organizations/${tenantId}/home`;
        } else {
          toast.error('Failed to start impersonation');
          navigate('/admin');
        }
      } catch (error: unknown) {
        const errorMessage =
          (error as { body?: { message?: string } })?.body?.message ??
          'Failed to start impersonation';
        toast.error(errorMessage);
        navigate('/admin');
      }
    };

    // Prevent double execution in StrictMode
    if (didImpersonate.current === false) {
      didImpersonate.current = true;
      performImpersonation();
    }
  }, [navigate, apiClient, searchParams, toast]);

  return (
    <Box className="grid h-screen place-items-center">
      <VStack gap={4} className="items-center">
        <Loader2 className="size-8 animate-spin text-border-accent-quaternary" />
        <Text size="sm" color="secondary">
          Starting impersonation...
        </Text>
      </VStack>
    </Box>
  );
};
